<template>
  <div class="v3-widget v3-panel v3-dist" v-testid="testid">
    <div class="v3-panel-head">
      <h3 v-testid="testid + '-title'">{{ title }}</h3>
      <span v-if="bc.resolvedRows.value.length" class="sub">{{ bc.total.value.toLocaleString() }}{{ definition.unitLabel ? ' ' + definition.unitLabel : '' }}</span>
    </div>

    <div v-if="bc.resolvedFilters.value.length" class="v3-dist-filters">
      <v-menu v-for="f in bc.resolvedFilters.value" :key="f.key" offset-y :close-on-content-click="!f.multiple">
        <template #activator="{ on, attrs }">
          <button class="v3-dist-filter" type="button" v-testid="testid + '-filter-' + f.key" v-on="on" v-bind="attrs">
            {{ bc.filterLabel(f) }}<v-icon small>expand_more</v-icon>
          </button>
        </template>
        <v-list dense class="v3-dist-menu">
          <v-list-item v-testid="testid + '-filter-' + f.key + '-all'" @click="bc.clearFilter(f)">
            <v-list-item-title>All</v-list-item-title>
          </v-list-item>
          <v-list-item v-for="o in bc.optionsFor(f)" :key="bc.optVal(o)" v-testid="testid + '-filter-' + f.key + '-opt-' + bc.optVal(o)" @click="bc.pickFilter(f, bc.optVal(o))">
            <v-list-item-icon v-if="f.multiple" class="mr-2">
              <v-icon small>{{ bc.isSelected(f, o) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}</v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ bc.optText(o) }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <div v-if="bc.resolvedRows.value.length" class="v3-dist-insight">
      Peaks at <strong>{{ bc.peak.value.label }}</strong> ({{ Number(bc.peak.value.value).toLocaleString() }})
    </div>

    <V3Skeleton v-if="bc.loading.value && !bc.resolvedRows.value.length" shape="bar" :heading="false" />
    <div v-else class="v3-dist-chart" role="img" :aria-label="title">
      <div v-for="row in bc.resolvedRows.value" :key="row.label" class="v3-dist-col" v-testid="testid + '-row-' + row.label" :title="row.label + ' · ' + row.value">
        <span class="v3-dist-n">{{ Number(row.value).toLocaleString() }}</span>
        <span class="v3-dist-bar" :style="{ height: bc.barH(row.value), background: bc.barColour.value }"></span>
        <span class="v3-dist-lv">{{ row.label }}</span>
      </div>
      <div v-if="!bc.resolvedRows.value.length" class="v3-bar-empty" v-testid="testid + '-empty'">No data.</div>
    </div>
    <div v-if="bc.categoryLabel.value" class="v3-dist-axis">{{ bc.categoryLabel.value }}</div>

    <button v-if="bc.linkText.value && linkAllowed" type="button" class="v3-prog-link" v-testid="testid + '-link'" @click="onLink">
      {{ bc.linkText.value }}<span class="ms">chevron_right</span>
    </button>
  </div>
</template>

<script>
import axios from "axios";
import { barChart } from "@rm-ui/homepage/barChart";
import { canNavigateTo } from "@/composables/useV3Nav";
import V3Skeleton from "./cLandingV3Skeleton";

// New-portal (v3) BarChart / Distribution — thin Vue 2 presentation over the
// shared `barChart` composable (@rm-ui/homepage/barChart). Data + filters +
// fetch live in the composable; this file owns the Vuetify v-menu filter
// dropdowns + navigation. Sibling presentation: rm-portal-beta BarChartWidget.vue.
export default {
  name: "cLandingV3BarChart",
  components: { V3Skeleton },
  props: {
    definition: { type: Object, default: () => ({}) },
    editMode: { type: Boolean, default: false },
    qaActions: { type: Array, default: () => [] },
  },
  setup(props, { emit }) {
    const bc = barChart({
      http: axios,
      getConfig: () => {
        const d = props.definition || {};
        return {
          title: d.title || "Distribution",
          rows: d.rows || [],
          filters: d.filters || [],
          barColor: d.barColor || "",
          categoryLabel: d.categoryLabel || "",
          dataSource: d.dataSource || "static",
          apiEndpoint: d.apiEndpoint || "",
          apiMethod: d.apiMethod || "POST",
          apiParams: d.apiParams || [],
          rowMapping: d.rowMapping || { labelField: "label", valueField: "value" },
          linkText: d.linkText || "",
          linkUrl: d.linkUrl || "",
        };
      },
      getEditMode: () => props.editMode,
      onError: (err) => {
        if (err && err.response && err.response.status === 401) emit("sessionExpired", err);
      },
    });
    return { bc };
  },
  computed: {
    testid() {
      return this.definition.controlType || "v3BarChart";
    },
    title() {
      return this.definition.title || "Distribution";
    },
    // Footer link is permission-gated (legacy landing-config parity); the
    // editor shows it regardless.
    linkAllowed() {
      const dest = this.definition.linkUrl || "";
      return this.editMode || !dest || canNavigateTo(this, dest);
    },
  },
  methods: {
    onLink() {
      if (this.editMode) return;
      const dest = this.definition.linkUrl;
      if (!dest) return;
      if (/^https?:\/\//i.test(dest)) {
        window.open(dest, "_blank");
        return;
      }
      this.$router.push({ name: dest }).catch(() => {});
    },
  },
};
</script>

<style lang="scss" src="./v3Widgets.scss"></style>
